import { Fragment, useCallback } from "react"
import type { FC, ReactElement } from "react"

import { QueryClient, QueryKey, useQueryClient } from "@tanstack/react-query"
import isUndefined from "lodash-es/isUndefined"
import { useMount, useShallowCompareEffect } from "react-use"

import { ECoachingSessionsIndividualQueryKey } from "@/3514/config"
import { AttendanceModal, SessionScheduleModal } from "@/3514/modals"
import { assistantSwapSessionPagePreloader } from "@/3514/pages/assistant.swap-session"
import {
    type TCoachingSessionsIndividualSuspenseQuery,
    coachingSessionsQueryLiveSessionAdder,
    coachingSessionsQueryLiveSessionRemover,
    useCoachingSessionsIndividualSuspenseQuery
} from "@/3514/queries"
import { useAnyModalVisible } from "@/3514/store/modal"
import { minutes } from "@/3514/utils"
import type { TEmptyAsyncCallback } from "@/shared/types/functions"

import CoachingSessionsView from "../coaching-sessions.view"
import modals, { CoachingSessionsScheduleAvailableModalOpener as ScheduleAvailableModalOpener } from "../modals"
import type { TCoachingSessionsEntityIndividualProps as TProps } from "../types"

const CoachingSessionsContainerIndividual: FC<TProps> = ({
    user,
    sessionInfoViewerRole,
    onMount = undefined,
    onQuerySuccess = undefined,
    wrapperClassName = undefined,
    withSwappableSessions = false,
    withCancellableSessions = false,
    withAttendanceMarkableSessions = false,
    withPostSessionModalOpeners = false,
    withGiftSessions = false
}: TProps): ReactElement => {
    const queryKey: QueryKey = ECoachingSessionsIndividualQueryKey({ participantId: user.id })

    const isAnyModalVisible: boolean = useAnyModalVisible()

    useMount((): void => {
        if (user.areGuestSessionsEnabled) void assistantSwapSessionPagePreloader()

        if (!isUndefined(onMount)) onMount()
    })

    const qc: QueryClient = useQueryClient()

    const {
        data: sessions,
        dataUpdatedAt: sessionsUpdatedAt,
        isSuccess: isQuerySuccess
    }: TCoachingSessionsIndividualSuspenseQuery = useCoachingSessionsIndividualSuspenseQuery({
        refetchInterval: minutes(15),
        refetchOnWindowFocus: isAnyModalVisible ? true : "always",
        refetchOnMount: true,
        participantId: user.id
    })

    useShallowCompareEffect((): void => {
        if (isQuerySuccess) onQuerySuccess?.(sessions)
    }, [isQuerySuccess, sessions, onQuerySuccess])

    const onLiveSessionStarted: TEmptyAsyncCallback = useCallback(
        async (): Promise<void> => await coachingSessionsQueryLiveSessionAdder({ queryKey }, { qc }),
        [queryKey, qc]
    )

    const onLiveSessionPassed: TEmptyAsyncCallback = useCallback(
        async (): Promise<void> => await coachingSessionsQueryLiveSessionRemover({ queryKey }, { qc }),
        [queryKey, qc]
    )

    return (
        <CoachingSessionsView
            sessions={sessions}
            sessionsUpdatedAt={sessionsUpdatedAt}
            user={user}
            sessionsQueryKey={queryKey}
            sessionInfoRole={sessionInfoViewerRole}
            onLiveSessionStarted={onLiveSessionStarted}
            onLiveSessionPassed={onLiveSessionPassed}
            wrapperClassName={wrapperClassName}
            withPostSessionModalOpeners={withPostSessionModalOpeners}
            withSwappableSessions={withSwappableSessions}
            withGiftSessions={withGiftSessions}
            withAttendanceMarkableSessions={withAttendanceMarkableSessions}
            renderScheduler={(): ReactElement => (
                <ScheduleAvailableModalOpener
                    sessions={sessions.unscheduledSessions}
                    sessionsQueryKey={queryKey}
                    user={user}
                    participantId={user.id}
                />
            )}
            renderModals={(): ReactElement => (
                <Fragment>
                    <SessionScheduleModal />

                    {withAttendanceMarkableSessions && <AttendanceModal />}

                    <modals.ScheduleAvailableModal />

                    <modals.CancelledSessionModal />

                    <modals.SessionReschedulesModal />

                    <modals.LateEditSessionModal />

                    {withCancellableSessions && <modals.CancelSessionModal />}

                    {withGiftSessions && (
                        <Fragment>
                            <modals.GiftSessionModal />

                            <modals.GiftedSessionModal />
                        </Fragment>
                    )}
                </Fragment>
            )}
        />
    )
}

CoachingSessionsContainerIndividual.displayName = "CoachingSessionsContainer"

export default CoachingSessionsContainerIndividual
